/**
 * TabContentSyncer - Syncs React Query data to Zustand store for open tabs
 * 
 * This component watches the metadataItem query for each open tab
 * and updates the Zustand store when server data changes.
 * Only syncs if the tab has no unsaved changes (isDirty = false).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import type { MetadataItem } from '@/services/metadata_v1/types';

export function TabContentSyncer() {
  const queryClient = useQueryClient();
  const tabs = useMetadataEditorStore((state) => state.tabs);
  const syncTabContentFromServer = useMetadataEditorStore((state) => state.syncTabContentFromServer);
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  useEffect(() => {
    if (!organizationId || tabs.length === 0) return;

    // Subscribe to query cache changes for all open tabs
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || !event.query.state.data) return;
      
      const queryKey = event.query.queryKey;
      
      // Check if this is a metadataItem query: ['metadataItem', orgId, fileId]
      if (
        queryKey[0] === 'metadataItem' &&
        queryKey[1] === organizationId &&
        typeof queryKey[2] === 'string'
      ) {
        const fileId = queryKey[2];
        const data = event.query.state.data as MetadataItem;
        
        // Check if this file is open in a tab
        const tab = tabs.find(t => t.file.id === fileId);
        if (tab && data?.content !== undefined) {
          // Sync the content (will only update if tab is not dirty)
          syncTabContentFromServer(fileId, data.content || '', data.isEdited);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, tabs, organizationId, syncTabContentFromServer]);

  // This component doesn't render anything
  return null;
}
